import {
  downloadXml,
  useEmitDocumentMutation,
  useDeleteDocument,
  useDuplicateDocument,
} from '@api/documents';
import Notification from '@components/notification/notification';
import { useAuth } from '@providers/auth';
import { useEffect, useState } from 'react';
import { useBulkSelection, BulkDownloadActions, type BulkSelection } from './bulkSelection';
import { useDisclosure } from '@mantine/hooks';
import { Link } from 'react-router-dom';
import {
  Loader,
  Table,
  ScrollArea,
  UnstyledButton,
  Group,
  Text,
  Center,
  ActionIcon,
  Menu,
  Skeleton,
  rem,
  HoverCard,
  Badge,
  Checkbox,
  Stack,
  Tooltip,
} from '@mantine/core';
import {
  IconBan,
  IconSelector,
  IconChevronDown,
  IconChevronUp,
  IconDots,
  IconEye,
  IconTrash,
  IconFileX,
  IconFileCheck,
  IconEdit,
  IconDownload,
  IconSend,
  IconCopy,
  IconLocationShare,
  IconCheck,
  IconFileAlert,
  IconBuildingStore,
} from '@tabler/icons-react';
import ConfirmationModal from '@components/ConfirmationModal/ConfirmationModal';
import { NoFoliosModal } from '@components/NoFoliosModal/NoFoliosModal';
// import DocumentDrawer from '@components/DocumentDrawer';
import NoPdfModal from '@pages/authenticated/documents/NoPdfModal';
import { VoidDocumentModal } from '@components/VoidDocumentModal/VoidDocumentModal';
import DocumentPrevisualizationModal from '@pages/authenticated/documents/previsualization/modal';
import styles from './TableSort.module.scss';
import { CommonHash, Caf } from '@types';
import AnswerDocumentModal from '@components/AnswerDocumentModal/AnswerDocumentModal';
import { ResendPDFModal } from '@components/ResendPDFModal/ResendPDFModal';
import AssignCostCenterModal from '@components/AssignCostCenterModal/AssignCostCenterModal';
import { useCostCenters } from '@api/costCenters';
import { CostCenter } from '@types';
import { capitalizeFirstLetter, isDocumentPayable, titleCase } from '@utils';

function buildCostCenterLabel(id: number, items: CostCenter[]): string {
  const current = items.find((c) => c.id === id);
  if (!current) return '';
  if (current.level === 1) return current.name;
  const parent = items.find((p) => p.id === current.parent_id);
  return parent ? `${parent.name} / ${current.name}` : current.name;
}
import { useCafs } from '@api/cafs';
import { BadgeWithHoverCard } from '@components/BadgeWithHoverCard/BadgeWithHoverCard';
import { CedeDocumentModal } from '@components/CedeDocumentModal/CedeDocumentModal';
import { PayDocumentModal } from '@components/PayDocumentModal/PayDocumentModal';

interface RowData {
  company: string;
  dte_type: string;
  number: number;
  total_amount: number;
  issue_date: string;
  sii_status: string;
}

interface ThProps {
  children: React.ReactNode;
  direction: string;
  sorted: boolean;
  onSort(): void;
}

function Th({ children, direction, sorted, onSort }: ThProps) {
  const Icon = sorted ? (direction === 'ASC' ? IconChevronUp : IconChevronDown) : IconSelector;
  return (
    <Table.Th className={styles.th}>
      <UnstyledButton onClick={onSort} className={styles.control}>
        <Group justify="space-between">
          <Text fw={800} fz="sm">
            {children}
          </Text>
          <Center className={styles.icon}>
            <Icon style={{ width: rem(16), height: rem(16) }} stroke={1.5} />
          </Center>
        </Group>
      </UnstyledButton>
    </Table.Th>
  );
}

interface DocsProps {
  docsProps: any;
  isFetching: boolean;
  sortKey: string;
  kind: string;
  direction: string;
  setSortKey: React.Dispatch<React.SetStateAction<string>>;
  setDirection: React.Dispatch<React.SetStateAction<string>>;
  editable?: boolean;
  emitable?: boolean;
  deletable?: boolean;
  bulkSelection?: BulkSelection;
}

const formatAmount = (number: number) => {
  if (typeof number === 'number') {
    return '$' + number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  } else {
    return number;
  }
};

function PaymentStatusBadge({ row }: { row: CommonHash }) {
  const isPayable = isDocumentPayable(row.type, row.status);

  if (!isPayable) {
    return (
      <Badge color="gray" variant="light">
        -
      </Badge>
    );
  }

  if (row.paid) {
    const parts: string[] = [];
    if (row.paid_at) {
      const formattedDate = new Date(row.paid_at + 'T00:00:00').toLocaleDateString('es-CL', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
      parts.push(`Fecha: ${formattedDate}`);
    } else {
      parts.push('Fecha: Sin fecha');
    }
    parts.push(row.payment_method?.name ? `Método: ${titleCase(row.payment_method.name)}` : 'Método: Sin método');
    return (
      <Tooltip label={parts.join(' · ')} withArrow>
        <Badge color="green" variant="light" style={{ cursor: 'default' }}>
          Pagado
        </Badge>
      </Tooltip>
    );
  }

  return (
    <Badge color="yellow" variant="light">
      Pendiente
    </Badge>
  );
}

export function TableSort({
  docsProps,
  isFetching,
  kind,
  sortKey,
  direction,
  setDirection,
  setSortKey,
  editable = true,
  emitable = true,
  deletable = true,
  bulkSelection,
}: DocsProps) {
  const [sortedData, setSortedData] = useState(docsProps);
  const { data: cafs } = useCafs();
  const fallbackSelection = useBulkSelection();
  const selection = bulkSelection ?? fallbackSelection;
  const {
    selectedUuids,
    toggleSelection,
    toggleSelectAll,
    clearSelection,
    setAllUuids,
    allSelected,
    someSelected,
  } = selection;

  useEffect(() => {
    setSortedData(docsProps);
    const uuids: string[] = docsProps?.map((row: any) => row.uuid) ?? [];
    setAllUuids(uuids);
    clearSelection();
  }, [docsProps, setAllUuids, clearSelection]);

  const setSorting = (field: keyof RowData) => {
    setSortKey(field);
    setDirection(direction === 'ASC' ? 'DESC' : 'ASC');
  };

  let rows;
  if (isFetching || sortedData === undefined) {
    rows = Array.from({ length: 5 }, (_, index) => (
      <Table.Tr key={index}>
        <Table.Td />
        <Table.Td>
          <Skeleton visible={isFetching} height={rem(30)} />
        </Table.Td>
        <Table.Td>
          <Skeleton visible={isFetching} height={rem(30)} />
        </Table.Td>
        <Table.Td>
          <Skeleton visible={isFetching} height={rem(30)} />
        </Table.Td>
        <Table.Td>
          <Skeleton visible={isFetching} height={rem(30)} />
        </Table.Td>
        <Table.Td>
          <Skeleton visible={isFetching} height={rem(30)} />
        </Table.Td>
        <Table.Td>
          <Skeleton visible={isFetching} height={rem(30)} />
        </Table.Td>
        <Table.Td>
          <Skeleton visible={isFetching} height={rem(30)} />
        </Table.Td>
        <Table.Td>
          <Skeleton visible={isFetching} height={rem(30)} />
        </Table.Td>
        <Table.Td>
          <Skeleton visible={isFetching} height={rem(30)} />
        </Table.Td>
      </Table.Tr>
    ));
  } else {
    rows = sortedData.map((row: any) => (
      <TableRow
        row={row}
        kind={kind}
        key={row.uuid}
        cafs={cafs?.data}
        editable={editable}
        emitable={emitable}
        deletable={deletable}
        isSelected={selectedUuids.has(row.uuid)}
        onToggle={() => toggleSelection(row.uuid)}
      />
    ));
  }

  return (
    <>
      {bulkSelection === undefined && <BulkDownloadActions bulkSelection={selection} />}
      <ScrollArea>
        <Table
          horizontalSpacing="md"
          verticalSpacing="md"
          miw={700}
          highlightOnHover
          withTableBorder
        >
          <Table.Thead>
            <Table.Tr>
              <Table.Th>
                <Checkbox
                  checked={allSelected}
                  indeterminate={someSelected && !allSelected}
                  onChange={toggleSelectAll}
                />
              </Table.Th>
              <Table.Th>RUT</Table.Th>
              <Th
                sorted={sortKey === 'company'}
                direction={direction}
                onSort={() => setSorting('company')}
              >
                Razón Social
              </Th>
              <Table.Th>Tipo de documento</Table.Th>
              <Th
                sorted={sortKey === 'number'}
                direction={direction}
                onSort={() => setSorting('number')}
              >
                Folio
              </Th>
              <Th
                sorted={sortKey === 'total_amount'}
                direction={direction}
                onSort={() => setSorting('total_amount')}
              >
                Monto
              </Th>
              <Table.Th>Estado</Table.Th>
              <Th
                sorted={sortKey === 'issue_date'}
                direction={direction}
                onSort={() => setSorting('issue_date')}
              >
                Fecha de Emisión
              </Th>
              <Table.Th>Pago</Table.Th>
              <Table.Th>Acciones</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {rows.length > 0 ? (
              rows
            ) : (
              <Table.Tr>
                <Table.Td colSpan={10}>
                  <Text fw={500} ta="center">
                    No hay documentos
                  </Text>
                </Table.Td>
              </Table.Tr>
            )}
          </Table.Tbody>
        </Table>
      </ScrollArea>
    </>
  );
}

const TableRow = ({
  row,
  kind,
  cafs,
  editable = true,
  emitable = true,
  deletable = true,
  isSelected,
  onToggle,
}: {
  row: CommonHash;
  kind: string;
  cafs?: Caf[];
  editable?: boolean;
  emitable?: boolean;
  deletable?: boolean;
  isSelected: boolean;
  onToggle: () => void;
}): React.ReactElement => {
  const { session: tableRowSession } = useAuth();
  const costCentersAvailable = !!tableRowSession.activeAccount?.cost_centers_available;
  const [openedCreditNoteModal, { open: openCreditNoteModal, close: closeCreditNoteModal }] =
    useDisclosure(false);
  const [
    openedAnswerDocumentModal,
    { open: openAnswerDocumentModal, close: closeAnswerDocumentModal },
  ] = useDisclosure(false);
  const [openedResendPDFModal, { open: openResendPDFModal, close: closeResendPDFModal }] =
    useDisclosure(false);
  const [openedNoPdfModal, { open: openNoPdfModal, close: closeNoPdfModal }] = useDisclosure(false);
  const [acceptDocument, setAcceptDocument] = useState(true);
  const [openedCedeDocumentModal, { open: openCedeDocumentModal, close: closeCedeDocumentModal }] =
    useDisclosure(false);
  const [openedPayDocumentModal, { open: openPayDocumentModal, close: closePayDocumentModal }] =
    useDisclosure(false);
  const [
    openedCostCenterModal,
    { open: openCostCenterModal, close: closeCostCenterModal },
  ] = useDisclosure(false);
  const { data: costCentersData } = useCostCenters();
  const allCostCenters = costCentersData?.data ?? [];
  const canEmit = () => {
    const currentCaf = cafs?.find(
      (caf) => capitalizeFirstLetter(caf.document) == capitalizeFirstLetter(row.dte_type),
    );
    if (currentCaf !== undefined && currentCaf.left_to_use > 0) {
      return true;
    }
    return false;
  };

  const isDateBeforeToday = (dateString: string): boolean => {
    if (!dateString) return false;

    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time to start of day

    const documentDate = new Date(dateString);
    documentDate.setHours(0, 0, 0, 0); // Reset time to start of day

    return documentDate < today;
  };

  const isDraftWithPastDate = row.status === 'draft' && isDateBeforeToday(row.issue_date);

  return (
    <>
      <Table.Tr
        key={row.uuid}
        style={{
          backgroundColor: isDraftWithPastDate ? 'var(--mantine-color-red-2)' : undefined,
        }}
      >
        <Table.Td>
          <Checkbox checked={isSelected} onChange={onToggle} />
        </Table.Td>
        <Table.Td>{row.company.tax_id}</Table.Td>
        <Table.Td>
          <Stack gap={2}>
            <Text fz="sm">{row.company_additional_info ? row.company_additional_info : row.company.legal_name}</Text>
            {costCentersAvailable && row.cost_center && (
              <Tooltip label={buildCostCenterLabel(row.cost_center.id, allCostCenters) || row.cost_center.name} withArrow position="top">
                <Badge variant="dot" color="violet" size="xs" maw={160} style={{ overflow: 'hidden', textOverflow: 'ellipsis', cursor: 'default' }}>
                  {buildCostCenterLabel(row.cost_center.id, allCostCenters) || row.cost_center.name}
                </Badge>
              </Tooltip>
            )}
          </Stack>
        </Table.Td>
        <Table.Td>{capitalizeFirstLetter(row.dte_type)}</Table.Td>
        <Table.Td>
          <HoverCard shadow="md" position="bottom" withArrow>
            <HoverCard.Target>
              <Text style={{ cursor: 'pointer' }}>{row.number}</Text>
            </HoverCard.Target>
            <HoverCard.Dropdown>
              <Text size="sm">
                {row.lines_names?.length > 0 ? row.lines_names : 'No hay detalles disponibles'}
              </Text>
            </HoverCard.Dropdown>
          </HoverCard>
        </Table.Td>
        <Table.Td>{formatAmount(row.total_amount)}</Table.Td>
        <Table.Td>
          <BadgeWithHoverCard row={row} />
        </Table.Td>
        <Table.Td>{row.issue_date}</Table.Td>
        <Table.Td>
          <PaymentStatusBadge row={row} />
        </Table.Td>
        <Table.Td>
          {row.status === 'draft' ? (
            <DraftMenu
              row={row}
              canEmit={canEmit()}
              editable={editable}
              emitable={emitable}
              deletable={deletable}
              openCostCenterModal={openCostCenterModal}
            />
          ) : (
            <NormalMenu
              row={row}
              kind={kind}
              openNoPdfModal={openNoPdfModal}
              openCreditNoteModal={openCreditNoteModal}
              openAnswerDocumentModal={openAnswerDocumentModal}
              openResendPDFModal={openResendPDFModal}
              openCedeDocumentModal={openCedeDocumentModal}
              openPayDocumentModal={openPayDocumentModal}
              openCostCenterModal={openCostCenterModal}
              setAcceptDocument={setAcceptDocument}
            />
          )}
        </Table.Td>
      </Table.Tr>
      <VoidDocumentModal opened={openedCreditNoteModal} close={closeCreditNoteModal} row={row} />
      <NoPdfModal opened={openedNoPdfModal} close={closeNoPdfModal} />
      <CedeDocumentModal
        opened={openedCedeDocumentModal}
        close={closeCedeDocumentModal}
        row={row}
      />
      <AnswerDocumentModal
        opened={openedAnswerDocumentModal}
        close={closeAnswerDocumentModal}
        acceptDocument={acceptDocument}
        uuid={row.uuid}
      />
      <ResendPDFModal opened={openedResendPDFModal} close={closeResendPDFModal} row={row} />
      <PayDocumentModal opened={openedPayDocumentModal} close={closePayDocumentModal} row={row} />
      <AssignCostCenterModal
        opened={openedCostCenterModal}
        close={closeCostCenterModal}
        uuids={[row.uuid]}
        currentCostCenterId={row.cost_center?.id}
      />
      {/* <ConfirmationModal
        apiFunction={deleteDocument}
        opened={openedConfirmationModal}
        close={closeConfirmationModal}
      /> */}
    </>
  );
};

const SeeDocument = ({
  row,
  openNoPdfModal,
  emittedByUs,
}: {
  row: CommonHash;
  openNoPdfModal: () => void;
  emittedByUs: () => boolean;
}): React.ReactElement => {
  const { mutate: duplicateDocument } = useDuplicateDocument();

  if (row.link === null && row.status === 'received') {
    return (
      <Menu.Item
        leftSection={<IconEye style={{ width: rem(16), height: rem(16) }} stroke={1.5} />}
        onClick={openNoPdfModal}
      >
        Ver documento
      </Menu.Item>
    );
  } else if (
    row.link === null &&
    row.status === 'sent' &&
    row.sii_status !== 'sii_rejected' &&
    row.sii_status !== 'company_received'
  ) {
    return (
      <Menu.Item
        leftSection={<Loader size="xs" type="dots" />}
        component="a"
        href={`${row.link}`}
        target="_blank"
      >
        Generando factura
      </Menu.Item>
    );
  } else if (row.link !== null) {
    return (
      <>
        {row.has_xml && (
          <Menu.Item
            leftSection={<IconEye style={{ width: rem(16), height: rem(16) }} stroke={1.5} />}
            component="a"
            href={row.link}
            target="_blank"
          >
            Ver documento
          </Menu.Item>
        )}
        {row.status === 'sent' &&
          row.type !== 'Documents::ElectronicDebitNote' &&
          row.type !== 'Documents::ElectronicCreditNote' &&
          emittedByUs() && (
            <Menu.Item
              leftSection={<IconCopy style={{ width: rem(16), height: rem(16) }} stroke={1.5} />}
              onClick={() => duplicateDocument({ uuid: row.uuid })}
            >
              Duplicar documento
            </Menu.Item>
          )}
        {!emittedByUs() && row.status === 'sent' && (
          <Menu.Item
            leftSection={<IconFileAlert style={{ width: rem(16), height: rem(16) }} stroke={1.5} />}
          >
            Documento externo
          </Menu.Item>
        )}
      </>
    );
  } else {
    return <></>;
  }
};

const DraftMenu = ({
  row,
  canEmit,
  editable = true,
  emitable = true,
  deletable = true,
  openCostCenterModal,
}: {
  row: CommonHash;
  canEmit: boolean;
  editable?: boolean;
  emitable?: boolean;
  deletable?: boolean;
  openCostCenterModal: () => void;
}) => {
  const { session: draftMenuSession } = useAuth();
  const draftCostCentersAvailable = !!draftMenuSession.activeAccount?.cost_centers_available;
  const { mutate: EmitDocument, isLoading: isEmitting } = useEmitDocumentMutation();
  const { mutate: DeleteDocument } = useDeleteDocument();
  const [
    openedDeleteConfirmationModal,
    { open: openDeleteConfirmationModal, close: closeDeleteConfirmationModal },
  ] = useDisclosure(false);
  const [
    openedEmitConfirmationModal,
    { open: openEmitConfirmationModal, close: closeEmitConfirmationModal },
  ] = useDisclosure(false);
  const [openedNoFoliosModal, { open: openNoFoliosModal, close: closeNoFoliosModal }] =
    useDisclosure(false);
  const [
    openedPrevisualizationModal,
    { open: openPrevisualizationModal, close: closePrevisualizationModal },
  ] = useDisclosure(false);
  return (
    <Menu transitionProps={{ transition: 'pop' }} withArrow position="bottom-end" withinPortal>
      <Menu.Target>
        <ActionIcon variant="subtle" color="gray">
          <IconDots style={{ width: rem(16), height: rem(16) }} stroke={1.5} />
        </ActionIcon>
      </Menu.Target>
      <Menu.Dropdown>
        <Menu.Item
          leftSection={<IconEdit style={{ width: rem(16), height: rem(16) }} stroke={1.5} />}
          component={Link}
          to={`/${String(row.uuid)}/editar`}
          disabled={!editable}
        >
          Editar
        </Menu.Item>
        <Menu.Item
          leftSection={<IconFileCheck style={{ width: rem(16), height: rem(16) }} stroke={1.5} />}
          onClick={() => {
            if (canEmit) openEmitConfirmationModal();
            else openNoFoliosModal();
          }}
          disabled={!emitable || isEmitting}
        >
          {isEmitting ? 'Emitiendo...' : 'Emitir'}
        </Menu.Item>
        <Menu.Item
          leftSection={<IconEye style={{ width: rem(16), height: rem(16) }} stroke={1.5} />}
          onClick={openPrevisualizationModal}
        >
          Previsualizar
        </Menu.Item>
        {draftCostCentersAvailable && (
          <Menu.Item
            leftSection={<IconBuildingStore style={{ width: rem(16), height: rem(16) }} stroke={1.5} />}
            onClick={openCostCenterModal}
          >
            Plan de cuenta
          </Menu.Item>
        )}
        <Menu.Item
          leftSection={<IconTrash style={{ width: rem(16), height: rem(16) }} stroke={1.5} />}
          onClick={openDeleteConfirmationModal}
          disabled={!deletable}
        >
          Eliminar
        </Menu.Item>
      </Menu.Dropdown>
      <ConfirmationModal
        opened={openedEmitConfirmationModal}
        close={closeEmitConfirmationModal}
        apiFunction={() => EmitDocument({ uuid: row.uuid })}
        title="¿Emitir documento?"
        description="Esta acción no se puede deshacer. El documento será enviado al SII."
        confirmLabel="Si, emitir"
        variant="confirm"
      />
      <ConfirmationModal
        opened={openedDeleteConfirmationModal}
        close={closeDeleteConfirmationModal}
        apiFunction={() => DeleteDocument({ uuid: row.uuid })}
        id={row.uuid}
      />
      <NoFoliosModal
        opened={openedNoFoliosModal}
        close={closeNoFoliosModal}
        dteType={capitalizeFirstLetter(row.dte_type) || ''}
      />
      <DocumentPrevisualizationModal
        opened={openedPrevisualizationModal}
        close={closePrevisualizationModal}
        documentUuid={row.uuid}
        includeDraft
        key={row.uuid}
      />
    </Menu>
  );
};

interface NormalMenuProps {
  row: CommonHash;
  kind: string;
  openNoPdfModal: () => void;
  openCreditNoteModal: () => void;
  openAnswerDocumentModal: () => void;
  openResendPDFModal: () => void;
  openCedeDocumentModal: () => void;
  openPayDocumentModal: () => void;
  openCostCenterModal: () => void;
  setAcceptDocument: React.Dispatch<React.SetStateAction<boolean>>;
}

const NormalMenu = ({
  row,
  kind,
  openNoPdfModal,
  openCreditNoteModal,
  openAnswerDocumentModal,
  openResendPDFModal,
  openCedeDocumentModal,
  openPayDocumentModal,
  openCostCenterModal,
  setAcceptDocument,
}: NormalMenuProps) => {
  const { session } = useAuth();
  const costCentersAvailableNormal = !!session.activeAccount?.cost_centers_available;
  const canEmitCreditNote = () => {
    if (
      kind === 'sent' &&
      !['sii_waiting', 'sii_rejected', 'canceled'].includes(row.sii_status) &&
      emittedByUs()
    )
      return true;
    return false;
  };

  const emittedByUs = (): boolean => {
    return row.status === 'sent' && row.has_xml;
  };

  const canResendDocument = (): boolean => {
    return (
      row.raw_xml !== null &&
      row.status === 'sent' &&
      row.sii_status !== 'sii_waiting' &&
      row.sii_status !== 'sii_rejected' &&
      row.sii_status !== 'sii_ceded' &&
      emittedByUs()
    );
  };

  const canMarkAsPaid = (): boolean => {
    return isDocumentPayable(row.type, row.status);
  };

  const getRawXml = async (uuid: string | undefined) => {
    if (!uuid || !session?.token) return;
    try {
      const { data } = await downloadXml(session.token, uuid);
      console.log(data.raw_xml);
      const element = document.createElement('a');
      const file = new Blob([data.raw_xml], { type: 'application/xml' });
      element.href = URL.createObjectURL(file);
      element.download = `${row.company.tax_id}_${row.number}.xml`;
      document.body.appendChild(element);
      element.click();
      document.body.removeChild(element);
    } catch (error) {
      Notification({ type: 'error', message: 'Error al descargar el XML, intente más tarde' });
    }
  };

  return (
    <Menu transitionProps={{ transition: 'pop' }} withArrow position="bottom-end" withinPortal>
      <Menu.Target>
        <ActionIcon variant="subtle" color="gray">
          <IconDots style={{ width: rem(16), height: rem(16) }} stroke={1.5} />
        </ActionIcon>
      </Menu.Target>
      <Menu.Dropdown>
        <SeeDocument row={row} openNoPdfModal={openNoPdfModal} emittedByUs={emittedByUs} />
        {row.has_xml && (
          <Menu.Item
            leftSection={<IconDownload style={{ width: rem(16), height: rem(16) }} stroke={1.5} />}
            onClick={() => getRawXml(row.uuid)}
          >
            Descargar XML
          </Menu.Item>
        )}
        {canResendDocument() && (
          <Menu.Item
            leftSection={<IconSend style={{ width: rem(16), height: rem(16) }} stroke={1.5} />}
            onClick={() => {
              openResendPDFModal();
            }}
          >
            Reenviar Documento
          </Menu.Item>
        )}
        {row.cedible && (
          <Menu.Item
            leftSection={
              <IconLocationShare style={{ width: rem(16), height: rem(16) }} stroke={1.5} />
            }
            onClick={() => {
              openCedeDocumentModal();
            }}
          >
            Ceder Documento
          </Menu.Item>
        )}
        {canMarkAsPaid() && (
          <Menu.Item
            color={row.paid ? 'red' : 'green'}
            leftSection={
              row.paid
                ? <IconBan style={{ width: rem(16), height: rem(16) }} stroke={1.5} />
                : <IconCheck style={{ width: rem(16), height: rem(16) }} stroke={1.5} />
            }
            onClick={() => {
              openPayDocumentModal();
            }}
          >
            {row.paid ? 'Marcar como No Pagado' : 'Marcar como Pagado'}
          </Menu.Item>
        )}
        {row.status === 'received' &&
          row.sii_status === 'company_received' &&
          row.can_answer_dte && (
            <>
              <Menu.Item
                leftSection={
                  <IconFileCheck style={{ width: rem(16), height: rem(16) }} stroke={1.5} />
                }
                color="green"
                onClick={() => {
                  setAcceptDocument(true);
                  openAnswerDocumentModal();
                }}
              >
                Aceptar
              </Menu.Item>
              <Menu.Item
                leftSection={<IconFileX style={{ width: rem(16), height: rem(16) }} stroke={1.5} />}
                color="red"
                onClick={() => {
                  setAcceptDocument(false);
                  openAnswerDocumentModal();
                }}
              >
                Rechazar
              </Menu.Item>
            </>
          )}
        {costCentersAvailableNormal && (
          <Menu.Item
            leftSection={<IconBuildingStore style={{ width: rem(16), height: rem(16) }} stroke={1.5} />}
            onClick={openCostCenterModal}
          >
            Plan de cuenta
          </Menu.Item>
        )}
        {canEmitCreditNote() && (
          <Menu.Item
            leftSection={<IconTrash style={{ width: rem(16), height: rem(16) }} stroke={1.5} />}
            color="red"
            onClick={openCreditNoteModal}
          >
            Anular Documento
          </Menu.Item>
        )}
      </Menu.Dropdown>
    </Menu>
  );
};
